'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProjectCardProps {
  project: {
    id: string;
    title: string;
    location: string;
    type: string;
    style: string;
    budget: string;
    area: string;
    image: string;
    alt: string;
    description: string;
    completionYear: number;
  };
  onViewDetails: (id: string) => void;
}

const ProjectCard = ({ project, onViewDetails }: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:border-[#1B5E4F]/30 hover:shadow-xl transition-all duration-500 hover:scale-[1.02]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="relative h-72 overflow-hidden cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onViewDetails(project.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onViewDetails(project.id); }}
      >
        <AppImage
          src={project.image}
          alt={project.alt}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-xs font-medium text-white">
            {project.completionYear}
          </span>
        </div>

        {/* Hover Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
          <p className="text-white/80 text-sm font-normal mb-3 line-clamp-2">
            {project.description}
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetails(project.id); }}
            className="inline-flex items-center gap-2 text-white font-medium text-sm hover:gap-3 transition-all duration-300"
          >
            <span>View Details</span>
            <Icon name="ArrowRightIcon" size={16} />
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-headline text-xl font-normal text-black mb-2 line-clamp-1 tracking-tight">
              {project.title}
            </h3>
            <div className="flex items-center gap-2 text-zinc-500 text-sm">
              <Icon name="MapPinIcon" size={14} />
              <span className="font-normal">{project.location}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-zinc-100">
          <div className="flex items-center gap-2">
            <Icon name="HomeIcon" size={14} className="text-[#1B5E4F]" />
            <span className="text-sm text-zinc-600 font-normal capitalize">{project.type}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="SparklesIcon" size={14} className="text-[#1B5E4F]" />
            <span className="text-sm text-zinc-600 font-normal capitalize">{project.style}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CurrencyRupeeIcon" size={14} className="text-[#1B5E4F]" />
            <span className="text-sm text-zinc-600 font-normal">{project.budget}L</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Square3Stack3DIcon" size={14} className="text-[#1B5E4F]" />
            <span className="text-sm text-zinc-600 font-normal">{project.area}</span>
          </div>
        </div>

        <button
          onClick={() => onViewDetails(project.id)}
          className="block w-full py-3 text-center bg-black text-white font-medium text-sm rounded-full hover:scale-105 transition-all duration-300 active:scale-95 min-h-[44px]"
        >
          Explore Project
        </button>
      </div>
    </div>
  );
};

export default ProjectCard;